import React, { useState, useEffect, useMemo } from "react";
import {
	Tabs,
	Tab,
	TextField,
	Button,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Typography,
	Box,
	Dialog, // Re-added Dialog
	DialogTitle, // Re-added DialogTitle
	DialogContent, // Re-added DialogContent
	MenuItem, // For Category and Sender Id dropdowns
	IconButton, // For Close button in DialogTitle
} from "@mui/material";
import { Search, ExpandMore, FileDownload, Close } from "@mui/icons-material"; // Re-added Close icon
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"; // Import plugin
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"; // Import plugin
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const getAmount = (network, sms) => {
	switch (network) {
		case "MTN":
		case "AIRTEL":
			return sms * 5.05;
		case "GLO":
			return sms * 4.9;
		case "9MOBILE":
			return sms * 2.95;
		case "INTERNATIONAL":
			return sms * 16.0;
		default:
			return 0;
	}
};

export default function SummaryDashboard() {
	const [tab, setTab] = useState(0);
	const [searchCostCentre, setSearchCostCentre] = useState("");
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [filterOpen, setFilterOpen] = useState(false);
	const [tempFromDate, setTempFromDate] = useState(null);
	const [tempToDate, setTempToDate] = useState(null);
	const [tempCostCenter, setTempCostCenter] = useState("");
	const [tempCategory, setTempCategory] = useState("");
	const [tempSenderId, setTempSenderId] = useState("");

	const [appliedFromDate, setAppliedFromDate] = useState(null);
	const [appliedToDate, setAppliedToDate] = useState(null);
	const [appliedCostCenter, setAppliedCostCenter] = useState("");
	const [appliedCategory, setAppliedCategory] = useState("");
	const [appliedSenderId, setAppliedSenderId] = useState("");
	const allCategories = useMemo(() => {
		return Array.from(new Set(data.map((item) => item.category)));
	}, [data]);

	const allSenderIds = useMemo(() => {
		return Array.from(new Set(data.map((item) => item.senderId)));
	}, [data]);

	const allCostCenters = useMemo(() => {
		return Array.from(new Set(data.map((item) => item.costCenter))).filter(
			Boolean,
		);
	}, [data]);
	useEffect(() => {
		async function fetchData() {
			const token = localStorage.getItem("token");
			try {
				const response = await fetch(
					"https://parallex.approot.ng/get_summary.php",
					{
						headers: { Authorization: `Bearer ${token}` },
					},
				);
				const result = await response.json();
				console.log(result);
				setData(result);
			} catch (err) {
				console.error("Error fetching data:", err);
				setError("Failed to load data");
			} finally {
				setLoading(false);
			}
		}

		fetchData();
	}, []);

	const filteredData = useMemo(() => {
		let filtered = data;

		if (searchCostCentre) {
			filtered = filtered.filter((item) =>
				item.costCenter.toLowerCase().includes(searchCostCentre.toLowerCase()),
			);
		}

		if (appliedFromDate && appliedToDate) {
			filtered = filtered.filter((item) => {
				const itemDate = dayjs(item.date);
				return (
					itemDate.isAfter(dayjs(appliedFromDate).subtract(1, "day")) &&
					itemDate.isBefore(dayjs(appliedToDate).add(1, "day"))
				);
			});
		} else if (appliedFromDate) {
			filtered = filtered.filter((item) =>
				dayjs(item.date).isSameOrAfter(dayjs(appliedFromDate), "day"),
			);
		} else if (appliedToDate) {
			filtered = filtered.filter((item) =>
				dayjs(item.date).isSameOrBefore(dayjs(appliedToDate), "day"),
			);
		}

		if (appliedCostCenter) {
			filtered = filtered.filter((item) =>
				item.costCenter.toLowerCase().includes(appliedCostCenter.toLowerCase()),
			);
		}

		if (appliedCategory) {
			filtered = filtered.filter((item) => item.category === appliedCategory);
		}

		if (appliedSenderId) {
			filtered = filtered.filter((item) => item.senderId === appliedSenderId);
		}

		if (tab === 1) {
			filtered = filtered
				.map((item) => ({
					...item,
					networks: item.networks.filter((n) => n.name === "INTERNATIONAL"),
				}))
				.filter((item) => item.networks.length > 0);
		}

		return filtered;
	}, [
		data,
		searchCostCentre,
		appliedFromDate,
		appliedToDate,
		appliedCostCenter,
		appliedCategory,
		appliedSenderId,
		tab,
	]);

	const handleApplyFilter = () => {
		setAppliedFromDate(tempFromDate);
		setAppliedToDate(tempToDate);
		setAppliedCostCenter(tempCostCenter);
		setAppliedCategory(tempCategory);
		setAppliedSenderId(tempSenderId);
		setFilterOpen(false);
	};

	const handleResetFilters = () => {
		setTempFromDate(null);
		setTempToDate(null);
		setTempCostCenter("");
		setTempCategory("");
		setTempSenderId("");
	};

	const exportToExcel = (data) => {
		const rows = [];
		let grandTotal = 0;

		data.forEach((item) => {
			const narration = "SMS ALERT MONTHLY VENDOR PAYMENT A/C";

			item.networks.forEach((net) => {
				const amount = getAmount(net.name, net.sms);
				grandTotal += amount;

				rows.push({
					Date: dayjs(item.date).format("YYYY-MM-DD"),
					Costcode: item.costCenter,
					Category: item.category || "",
					Network: net.name,
					Sms_Count: net.sms,
					Narration: narration,
					Amount: amount,
					Currency: "NGN",
				});
			});
		});

		// ✅ Only grand total row at the end
		rows.push({});
		rows.push({
			Costcode: "",
			Narration: "TOTAL",
			Amount: grandTotal,
			Currency: "NGN",
		});

		const worksheet = XLSX.utils.json_to_sheet(rows);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "SMS Summary");

		const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
		saveAs(
			new Blob([wbout], { type: "application/octet-stream" }),
			"sms_summary.xlsx",
		);
	};

	if (loading) return <Typography>Loading summary data...</Typography>;
	if (error) return <Typography color='error'>{error}</Typography>;

	return (
		<div className='p-4 bg-white rounded-md shadow-sm'>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				{/* Tabs */}
				<Tabs
					value={tab}
					onChange={(_, val) => setTab(val)}
					className='mb-4'
					sx={{
						"& .MuiTabs-indicator": {
							backgroundColor: "#D54B01",
							height: "3px",
						},
					}}
				>
					<Tab
						label='Summary'
						sx={{
							color: tab === 0 ? "#D54B01" : "#555",
							fontWeight: tab === 0 ? "600" : "normal",
							textTransform: "none",
							fontSize: "0.9rem",
							minWidth: "unset",
							padding: "6px 12px",
						}}
					/>
					<Tab
						label='International'
						sx={{
							color: tab === 1 ? "#D54B01" : "#555",
							fontWeight: tab === 1 ? "600" : "normal",
							textTransform: "none",
							fontSize: "0.9rem",
							minWidth: "unset",
							padding: "6px 12px",
						}}
					/>
				</Tabs>

				{/* Controls - Search, Filter Button, Export Button */}
				<Box className='flex flex-wrap items-center justify-between gap-3 mb-6'>
					{/* Search Cost Centre */}
					<TextField
						placeholder='Search cost centre'
						variant='outlined'
						size='small'
						value={searchCostCentre}
						onChange={(e) => setSearchCostCentre(e.target.value)}
						sx={{ minWidth: "220px", flexGrow: 1 }}
						InputProps={{
							startAdornment: <Search sx={{ mr: 1, color: "action.active" }} />,
							sx: { borderRadius: "8px", paddingLeft: "8px" },
						}}
					/>

					<Box className='flex items-center gap-3'>
						{/* Filter Button */}
						<Button
							variant='outlined'
							sx={{
								backgroundColor: "white",
								color: "black",
								borderColor: "#ccc",
								"&:hover": { backgroundColor: "#f1f1f1" },
								textTransform: "none",
								borderRadius: "8px",
								minWidth: "80px",
								padding: "8px 16px",
							}}
							onClick={() => {
								// When opening the filter modal, populate temp states with applied states
								setTempFromDate(appliedFromDate);
								setTempToDate(appliedToDate);
								setTempCostCenter(appliedCostCenter);
								setTempCategory(appliedCategory);
								setTempSenderId(appliedSenderId);
								setFilterOpen(true);
							}}
						>
							Filter
						</Button>
						{/* Export Button */}
						<Button
							variant='contained'
							startIcon={<FileDownload />}
							onClick={() => exportToExcel(filteredData)}
							sx={{
								backgroundColor: "#D54B01",
								"&:hover": { backgroundColor: "#b64000" },
								textTransform: "none",
								borderRadius: "8px",
								minWidth: "80px",
								padding: "8px 16px",
							}}
						>
							Export
						</Button>
					</Box>
				</Box>

				{/* Table Header */}
				<Box className='flex justify-between w-full mb-3 py-2 px-4 border-b border-gray-200'>
					<Typography
						variant='subtitle2'
						sx={{ width: "33%", fontWeight: "600" }}
					>
						Date
					</Typography>
					<Typography
						variant='subtitle2'
						sx={{ width: "33%", textAlign: "center", fontWeight: "600" }}
					>
						Total SMS sent
					</Typography>
					<Typography
						variant='subtitle2'
						sx={{ width: "33%", textAlign: "right", fontWeight: "600" }}
					>
						Amount
					</Typography>
				</Box>

				{/* Table View (Accordions) */}
				<div className='space-y-3'>
					{filteredData.length > 0 ? (
						filteredData.map((item, idx) => {
							const totalSms = item.networks.reduce((acc, n) => acc + n.sms, 0);
							const totalAmount = item.networks.reduce(
								(acc, n) => acc + getAmount(n.name, n.sms),
								0,
							);

							// Only render if totalSms is > 0 for International tab (after filtering networks)
							if (tab === 1 && totalSms === 0) {
								return null;
							}

							return (
								<Accordion
									key={idx}
									defaultExpanded={idx === 0}
									sx={{
										boxShadow: "none",
										border: "1px solid #e0e0e0",
										borderRadius: "8px",
										overflow: "hidden",
										"&:not(:last-child)": {
											marginBottom: "8px",
										},
										"&.Mui-expanded": {
											margin: "8px 0",
										},
										"& .MuiAccordionSummary-root": {
											padding: "12px 16px",
											minHeight: "48px",
										},
										"& .MuiAccordionDetails-root": {
											padding: "8px 16px 16px",
										},
									}}
								>
									<AccordionSummary
										expandIcon={<ExpandMore sx={{ color: "#D54B01" }} />}
										sx={{ flexDirection: "row-reverse" }}
									>
										<Box className='flex justify-between items-center w-full'>
											<Typography
												variant='body1'
												sx={{ width: "33%", fontWeight: "500" }}
											>
												{dayjs(item.date).format("MMMM D, YYYY")}
											</Typography>
											<Typography
												variant='body1'
												sx={{ width: "33%", textAlign: "center" }}
											>
												{totalSms.toLocaleString()}
											</Typography>
											<Typography
												variant='body1'
												sx={{
													width: "33%",
													textAlign: "right",
													fontWeight: "500",
												}}
											>
												₦
												{totalAmount.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</Typography>
										</Box>
									</AccordionSummary>
									{item.networks.length > 0 && (
										<AccordionDetails>
											<div className='space-y-1 ml-4 border-l border-gray-300 pl-4'>
												{item.networks.map((n, subIdx) => (
													<Box
														key={subIdx}
														className='flex justify-between text-sm text-gray-700 py-1'
													>
														<Typography variant='body2' sx={{ width: "33%" }}>
															{n.name}
														</Typography>
														<Typography
															variant='body2'
															sx={{ width: "33%", textAlign: "center" }}
														>
															{n.sms.toLocaleString()}
														</Typography>
														<Typography
															variant='body2'
															sx={{ width: "33%", textAlign: "right" }}
														>
															₦
															{getAmount(n.name, n.sms).toLocaleString(
																undefined,
																{
																	minimumFractionDigits: 2,
																	maximumFractionDigits: 2,
																},
															)}
														</Typography>
													</Box>
												))}
											</div>
										</AccordionDetails>
									)}
								</Accordion>
							);
						})
					) : (
						<Typography
							variant='body1'
							color='textSecondary'
							sx={{ textAlign: "center", mt: 4 }}
						>
							No data available for the selected filters.
						</Typography>
					)}
				</div>

				{/* Filter Modal (Re-introduced) */}
				<Dialog
					open={filterOpen}
					onClose={() => setFilterOpen(false)}
					maxWidth='xs' // Adjusted maxWidth to xs for a smaller modal
					fullWidth
					sx={{ "& .MuiDialog-paper": { borderRadius: "12px" } }} // Rounded corners for modal
				>
					<DialogTitle
						className='flex justify-between items-center'
						sx={{
							fontWeight: "600",
							fontSize: "1.1rem",
							pb: 1.5,
							pt: 2,
							px: 3,
						}}
					>
						<span>Filter by</span>
						<IconButton onClick={() => setFilterOpen(false)} sx={{ p: 0.5 }}>
							<Close />
						</IconButton>
					</DialogTitle>
					<DialogContent
						dividers
						sx={{ borderBottom: "none", px: 3, pt: 2, pb: 2 }}
						className='space-y-4'
					>
						<Typography variant='body2' color='textSecondary' sx={{ mb: 1 }}>
							Date
						</Typography>
						<Box className='grid grid-cols-2 gap-4'>
							{" "}
							{/* Use Box for grid */}
							<DatePicker
								label='From'
								value={tempFromDate}
								onChange={(val) => setTempFromDate(val)}
								slotProps={{ textField: { size: "small", fullWidth: true } }}
							/>
							<DatePicker
								label='To'
								value={tempToDate}
								onChange={(val) => setTempToDate(val)}
								slotProps={{ textField: { size: "small", fullWidth: true } }}
							/>
						</Box>
						{/* <TextField
							label='Cost Center'
							placeholder='Enter cost center'
							variant='outlined'
							fullWidth
							size='small' // Added size small
							value={tempCostCenter}
							onChange={(e) => setTempCostCenter(e.target.value)}
							InputProps={{
								endAdornment: tempCostCenter && (
									<Button
										onClick={() => setTempCostCenter("")}
										sx={{ textTransform: "none", minWidth: "unset", p: 0.5 }}
									>
										Clear
									</Button>
								),
							}}
						/> */}
						<TextField
							select
							label='Cost Center'
							fullWidth
							size='small'
							value={tempCostCenter}
							onChange={(e) => setTempCostCenter(e.target.value)}
						>
							<MenuItem value=''>All</MenuItem>
							{allCostCenters.map((cc) => (
								<MenuItem key={cc} value={cc}>
									{cc}
								</MenuItem>
							))}
						</TextField>
						<TextField
							select
							label='Category'
							fullWidth
							size='small' // Added size small
							value={tempCategory}
							onChange={(e) => setTempCategory(e.target.value)}
							InputProps={{
								endAdornment: tempCategory && (
									<Button
										onClick={() => setTempCategory("")}
										sx={{ textTransform: "none", minWidth: "unset", p: 0.5 }}
									>
										Clear
									</Button>
								),
							}}
						>
							<MenuItem value=''>All</MenuItem>{" "}
							{/* Option to select all/clear */}
							{allCategories.map((cat) => (
								<MenuItem key={cat} value={cat}>
									{cat}
								</MenuItem>
							))}
						</TextField>
						<TextField
							select
							label='Sender Id'
							fullWidth
							size='small' // Added size small
							value={tempSenderId}
							onChange={(e) => setTempSenderId(e.target.value)}
							InputProps={{
								endAdornment: tempSenderId && (
									<Button
										onClick={() => setTempSenderId("")}
										sx={{ textTransform: "none", minWidth: "unset", p: 0.5 }}
									>
										Clear
									</Button>
								),
							}}
						>
							<MenuItem value=''>All</MenuItem>{" "}
							{/* Option to select all/clear */}
							{allSenderIds.map((sid) => (
								<MenuItem key={sid} value={sid}>
									{sid}
								</MenuItem>
							))}
						</TextField>
						<Box className='flex justify-between items-center pt-4'>
							<Button
								variant='outlined'
								sx={{
									textTransform: "none",
									borderRadius: "8px",
									borderColor: "#ccc",
									color: "#555",
								}}
								onClick={handleResetFilters}
							>
								Reset
							</Button>
							<Button
								variant='contained'
								sx={{
									backgroundColor: "#D54B01",
									"&:hover": { backgroundColor: "#b64000" },
									textTransform: "none",
									borderRadius: "8px",
								}}
								onClick={handleApplyFilter}
							>
								Apply Filter
							</Button>
						</Box>
					</DialogContent>
				</Dialog>
			</LocalizationProvider>
		</div>
	);
}
